import React, { useState, useEffect } from "react";
import AvailabilityRow from "../components/AvailabilityRow";
import OverrideModal from "../components/OverrideModal";
import TimezoneSelector from "../components/TimezoneSelector";
import AvailabilitySummary from "../components/AvailabilitySummary";
import axios from "axios";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

export const showToast = (title, icon = "success") => {
  Swal.fire({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    icon,
    title,
  });
};

const weekdays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

axios.defaults.withCredentials = true;
axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem(
  "token"
)}`;

export default function AvailabilityPage({ userId }) {
  const [availability, setAvailability] = useState([]);
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/user/${userId}/availability`
        );
        console.log("calling API");
        if (response.data && response.data.availability.length > 0) {
          // Flatten the API structure to match what AvailabilityRow expects
          const flatSlots = response.data.availability.flatMap((day) =>
            day.slots.map((slot) => ({
              day: day.day,
              start: slot.start,
              end: slot.end,
              availability: slot.availability,
              enabled: slot.enabled !== undefined ? slot.enabled : true, // fallback to true
            }))
          );
          setAvailability(flatSlots);
        } else {
          // If no data is returned, initialize with default times
          initializeDefaultAvailability();
        }
      } catch (error) {
        console.error("Error fetching availability data", error);
        showToast("Failed to load availability.", "error");
        // Fallback to default availability in case of an error
        initializeDefaultAvailability();
      }
    };

    const initializeDefaultAvailability = () => {
      // Initialize availability with default times if not fetched from backend
      const initialAvailability = weekdays.reduce((acc, day) => {
        acc[day] = [{ start: "09:00", end: "17:00" }];
        return acc;
      }, {});
      setAvailability(initialAvailability);
    };

    fetchAvailability();
  }, [userId, weekdays]);

  const handleSave = async () => {
    const payload = {
      availability,
      timezone, // include selected timezone
    };
    await axios.put(
      `${API_BASE_URL}/user/${userId}/availability`,
      JSON.stringify({ payload })
    );
  };
  const getTotalWeeklyHours = () => {
    let totalMinutes = 0;
    availability.forEach((slot) => {
      if (!slot.enabled) return;
      const [startH, startM] = slot.start.split(":").map(Number);
      const [endH, endM] = slot.end.split(":").map(Number);
      const start = startH * 60 + startM;
      const end = endH * 60 + endM;
      totalMinutes += Math.max(end - start, 0);
    });
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="container p-3">
      {/* Header Section */}
      <div class="d-flex justify-content-between align-items-center mb-4 title-line-re">
        <h5 class="fw700 mb-0 color1">Availability Settings</h5>
      </div>

      <div className="row mb-4">
        <div className="col-md-6 mb-3 mb-md-0">
          <TimezoneSelector timezone={timezone} setTimezone={setTimezone} />
          <small className="text-muted">
            Set your weekly hours and override specific dates if needed.
          </small>
          <div
            className="p-0 mb-0"
            style={{
              background: "transparent",
              border: "0",
              boxShadow: "none",
            }}
          >
            {weekdays.map((day, idx) => (
              <div
                key={day}
                className={` ${
                  idx !== weekdays.length - 1 ? "border-bottom mb-3" : ""
                }`}
              >
                <AvailabilityRow
                  day={day}
                  availability={availability}
                  setAvailability={setAvailability}
                  readOnly={false}
                />
              </div>
            ))}

            {/* Weekly Total */}
            <div className="alert alert-success mt-4 p-2 twabx">
              <strong className="ms-2">Total Weekly Availability:</strong>{" "}
              <span className="fs20 fw700 color11">
                {getTotalWeeklyHours()}
              </span>
            </div>
          </div>
        </div>
        <div className="col-md-6 text-md-end">
          {/* Summary */}
          <div className="mb-4 mtc31 sticky-top">
            <AvailabilitySummary availability={availability} />
          </div>
        </div>
      </div>

      {/* Weekly Availability */}

      {/* Action Buttons */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 twabx mb-4">
        <button
          className="btn btn-outline-success w-100 w-md-auto"
          onClick={() => setShowModal(true)}
        >
          <i class="bi bi-calendar-plus-fill"></i> Add Date Override
        </button>

        <button
          className="btn btn-success w-100 w-md-auto mb-3"
          onClick={handleSave}
        >
          <i class="bi bi-floppy-fill"></i> Save Availability
        </button>
      </div>

      {/* Modal */}
      <OverrideModal
        userId={userId}
        open={showModal}
        onClose={() => setShowModal(false)}
      />
    </div>
  );
}
